import type {
  AllocationConstraintState,
  ModelBuilder,
} from '@/reducers/model-builder'

import { MaturityOption } from '@moment/horizon-data-access/proposal'
import { DEFAULT_FILTER_BUILDER_CONDITION } from '@moment/risk-controls-feature/filter-builder/constants'
import {
  CouponType,
  TreasurySubtype,
} from '@moment/trading-types/reference-data'

import { DiversificationMode } from '@/components/molecules/diversification-constraint/types'
import { RatingMode } from '@/types/enums/models'
import { TargetBandMode } from '@/types/enums/target-band.mode'

import { AllowBlockRequireEnum, AllowEnum } from './allow-map'

export enum BucketType {
  Months = 'months',
  Years = 'years',
}

export const DEFAULT_TARGET_BAND_STATE = {
  target: '',
  min: '',
  max: '',
  buffer: '',
  mode: TargetBandMode.Buffer,
}

export const DEFAULT_ALLOCATION_CONSTRAINT: AllocationConstraintState = {
  targetBand: DEFAULT_TARGET_BAND_STATE,
  conditions: [DEFAULT_FILTER_BUILDER_CONDITION],
}

export const DEFAULT_WEIGHT_TOLERANCE = 3

export const DEFAULT_MODEL_BUILDER_VALUES: ModelBuilder = {
  // Model Setup defaults
  id: null,
  name: '',
  description: '',
  cash: 1_000_000,
  assetClasses: [],
  sharing_organization_id: '',
  municipalState: null,
  municipalStatePreference: null,
  stateMinWeight: null,
  stateTargetWeight: null,
  stateMaxWeight: null,
  stateBuffer: null,
  showMinMax: false,
  /// ladder policy defaults
  bucketType: BucketType.Years,
  bucketWidth: 12,
  rangeInPeriods: [0, 10],
  bucketWeightMin: null,
  bucketWeightMax: null,
  bucketWeightBuffer: DEFAULT_WEIGHT_TOLERANCE,
  ladderMarketValueTolerance: null,
  useCallAdjustedMaturities: false,
  maturityOption: MaturityOption.maturity_date,
  ladderPolicyEnabled: true,
  // Universe defaults
  couponRange: [0, 20],
  ytwRange: [0, 20],
  priceRange: [0, 200],
  maturityRange: [0, 60],
  nextCallRange: [0, 60],
  workoutRange: [0, 60],
  liquidityScoreRange: [0, 5],
  durationRange: [0, 60],
  minSpRange: null,
  maxSpRange: null,
  spRatingMode: RatingMode.None,
  minLowestRatingRange: null,
  maxLowestRatingRange: null,
  lowestRatingMode: RatingMode.None,
  minMoodysRange: null,
  maxMoodysRange: null,
  moodysRatingMode: RatingMode.None,
  issuesSizeRange: [0, 100_000_000_000],
  couponType: [CouponType.Fixed, CouponType.Zero],
  treasurySubtype: [
    TreasurySubtype.Bill,
    TreasurySubtype.Note,
    TreasurySubtype.Bond,
  ],
  excludeSectors: [],
  excludeIssuers: [],
  includeTickers: [],
  excludeTickers: [],
  includeInstruments: [],
  municipalTaxableFederal: [AllowEnum.DontAllow],
  privatePlacement: [AllowEnum.DontAllow],
  regulationS: [AllowEnum.DontAllow],
  amtApplicable: [AllowEnum.DontAllow],
  belowDeMinimisThreshold: [AllowEnum.Allow],
  excludeUseOfProceeds: [],
  excludeMunicipalStates: [],
  includeCurrencies: ['USD'],
  includeCountryDomicile: [],
  excludeGicsSectorL4: [],
  excludeIceSectorL4: [],
  perpetual: false,
  sinkingFund: false,
  callable: false,
  includeFilterList: null,
  excludeFilterList: null,
  fdicInsured: [AllowEnum.Allow],
  vrdoIndicator: [AllowBlockRequireEnum.Allow],

  // Diversification defaults
  weightPerIssuer: {
    mode: DiversificationMode.Default,
    weightRange: [0, 100],
  },
  weightPerSector: {
    mode: DiversificationMode.Default,
    weightRange: [0, 100],
  },
  weightPerState: {
    mode: DiversificationMode.Default,
    weightRange: [0, 100],
  },
  weightPerBond: {
    mode: DiversificationMode.Default,
    weightRange: [2, 5],
  },
  weightPerMaturityYear: {
    mode: DiversificationMode.Default,
    weightRange: [0, 100],
  },
  weightPerFdicCode: {
    mode: DiversificationMode.Default,
    weightRange: [0, 250_000],
  },
  weightPerInsurer: {
    mode: DiversificationMode.Default,
    weightRange: [0, 100],
  },
  weightPerUltimateBorrower: {
    mode: DiversificationMode.Default,
    weightRange: [0, 100],
  },
  weightPerUltimateParent: {
    mode: DiversificationMode.Default,
    weightRange: [0, 100],
  },

  // target averages
  spRatingTarget: null,
  spRatingMin: null,
  spRatingMax: null,
  spRatingBuffer: null,
  ytwTarget: null,
  ytwMin: null,
  ytwMax: null,
  ytwBuffer: null,
  couponTarget: null,
  couponMin: null,
  couponMax: null,
  couponBuffer: null,

  // target averages
  spRatingTargetBand: null,
  lowestRatingTargetBand: null,
  moodysRatingTargetBand: null,
  couponTargetBand: null,
  durationTargetBand: null,
  weightedTargetBand: null,
  liquidityScoreTargetBand: null,
  convexityTargetBand: null,
  maturityTargetBand: null,

  // allocation constraints
  allocationConstraints: [DEFAULT_ALLOCATION_CONSTRAINT],

  // excludeStructures default
  excludeStructures: [],

  // constraint ID map for round-trip preservation
  constraintIdMap: {},
}
